import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IAnneeScolaire, defaultValue } from 'app/shared/model/anneeScolaire.model';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IAnneeScolaire>,
  entity: defaultValue,
  entityActive: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/annees-scolaires';

// Actions

export const getEcoleAnneeScolaire = createAsyncThunk(
  'anneesScolaires/fetch_ecole_annees_scolaires_list',
  async (ecoleId: string | number) => {
    const requestUrl = `${apiUrl}/ecole/${ecoleId}`;
    return axios.get<IAnneeScolaire[]>(requestUrl);
  }
);
export const getEcoleActiveAnneeScolaire = createAsyncThunk(
  'anneesScolaires/fetch_ecole_annees_scolaire_active',
  async (ecoleId: string | number) => {
    const requestUrl = `${apiUrl}/ecole/${ecoleId}/active`;
    return axios.get<IAnneeScolaire>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getAnneeScolaire = createAsyncThunk('anneesScolaires/fetch_annee_scolaire', async (id: string | number) => {
  const requestUrl = `${apiUrl}/${id}`;
  return axios.get<IAnneeScolaire>(requestUrl);
});

export const createAnneeScolaire = createAsyncThunk(
  'anneeScolaire/create_anneeScolaire',
  async (entity: IAnneeScolaire, thunkAPI) => {
    const result = await axios.post<IAnneeScolaire>(`${apiUrl}`, cleanEntity(entity));
    thunkAPI.dispatch(getEcoleAnneeScolaire(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateAnneeScolaire = createAsyncThunk(
  'eleve/update_annee_scolaire',
  async (entity: IAnneeScolaire, thunkAPI) => {
    const result = await axios.put<IAnneeScolaire>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEcoleAnneeScolaire(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteAnneeScolaire = createAsyncThunk(
  'eleve/update_annee_scolaire',
  async (entity: IAnneeScolaire, thunkAPI) => {
    const result = await axios.delete<IAnneeScolaire>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEcoleAnneeScolaire(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const startAnneeScolaire = createAsyncThunk(
  'anneeScolaire/start_annee_scolaire',
  async (id: string, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}/start`;
    const result = await axios.post<IAnneeScolaire>(requestUrl);
    thunkAPI.dispatch(getEcoleAnneeScolaire(result.data.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const cloturerAnneeScolaire = createAsyncThunk(
  'eleve/update_annee_scolaire',
  async (entity: IAnneeScolaire, thunkAPI) => {
    const result = await axios.put<IAnneeScolaire>(`${apiUrl}/${entity.id}/cloturer`, cleanEntity(entity));
    thunkAPI.dispatch(getEcoleAnneeScolaire(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice
export type AnneeScolaireState = Readonly<typeof initialState>;
export const AnneeScolaireSlice = createSlice({
  name: 'anneeScolaire',
  initialState: initialState as AnneeScolaireState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAnneeScolaire.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getEcoleAnneeScolaire), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEcoleActiveAnneeScolaire), (state, action) => {
        return {
          ...state,
          loading: false,
          entityActive: action.payload.data,
        };
      })
      .addMatcher(isFulfilled(createAnneeScolaire, updateAnneeScolaire, startAnneeScolaire, cloturerAnneeScolaire), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })

      .addMatcher(isPending(getEcoleAnneeScolaire, getAnneeScolaire, getEcoleActiveAnneeScolaire, startAnneeScolaire), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createAnneeScolaire, updateAnneeScolaire, startAnneeScolaire, cloturerAnneeScolaire), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(createAnneeScolaire, updateAnneeScolaire, startAnneeScolaire), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = false;
        state.loading = false;
      });
  },
});

export const { reset } = AnneeScolaireSlice.actions;

// Reducer
export default AnneeScolaireSlice.reducer;
